import { useState } from "react";

import type { MeResponse } from "../api/types";
import { initials } from "../lib/format";
import { ThemePicker } from "../themes/ThemePicker";
import { useTheme } from "../themes/ThemeProvider";

export function TopBar({
  me,
  pageTitle,
  onLogout,
  onToggleNav
}: {
  me: MeResponse;
  pageTitle: string;
  onLogout: () => Promise<void>;
  onToggleNav: () => void;
}) {
  const { theme } = useTheme();
  const [logoutPending, setLogoutPending] = useState(false);

  async function handleLogout() {
    setLogoutPending(true);
    try {
      await onLogout();
    } finally {
      setLogoutPending(false);
    }
  }

  return (
    <header className="topbar">
      <div className="topbar__brand">
        <button
          type="button"
          className="topbar__menu"
          aria-label="Open navigation"
          onClick={onToggleNav}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
        <BrandBadge label={theme.brandLabel} />
        <h1 className="topbar__title">{pageTitle}</h1>
      </div>
      <div className="topbar__actions">
        <ThemePicker />
        <div className="topbar__user" title={me.user.email}>
          {me.user.avatar_url !== "" ? (
            <img
              className="topbar__avatar topbar__avatar--img"
              src={me.user.avatar_url}
              alt=""
              aria-hidden="true"
            />
          ) : (
            <span className="topbar__avatar">{initials(me.user.name, me.user.email)}</span>
          )}
          <div className="topbar__user-text">
            <strong>{me.user.name === "" ? me.user.email : me.user.name}</strong>
            <span className="topbar__role">{me.membership.role}</span>
          </div>
        </div>
        <button
          type="button"
          className="btn btn--ghost"
          disabled={logoutPending}
          onClick={() => void handleLogout()}
        >
          {logoutPending ? "Signing out…" : "Sign out"}
        </button>
      </div>
    </header>
  );
}

function BrandBadge({ label }: { label: string }) {
  return (
    <span className="brand-badge" aria-label={`Theme: ${label}`}>
      <span className="brand-badge__crown" aria-hidden="true">♛</span>
      <span className="brand-badge__text">
        <span className="brand-badge__label">{label}</span>
        <span className="brand-badge__sub">Contribution Blocks</span>
      </span>
    </span>
  );
}
